/* eslint-disable max-lines */
import { FC, memo, useCallback } from 'react';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import ControlledTextField from '@components/ControlledTextField';
import { TranslationContentList, TranslationSetupForm } from '@customTypes';
import { useFormContext, useWatch } from 'react-hook-form';
import useCustomTranslation from '@hooks/useCustomTranslation';
import {
  useTranslateGeneralContentTranslationMutation,
  useTranslateProjectContentDataMutation,
} from '@services/api/translation.api';
import {
  DEFAULT_ICON_WIDTH,
  PROJECT_SETUP_LANGUAGE_OPTIONS,
  SETUP_LANGUAGE_TABS,
  TRANSLATION_MAX_LENGTH,
} from '@constants';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks';
import { truncateString } from '@utils/helpers';
import { setSetupLanguageChanges } from '@store/reducers/project.reducer';

import styles from './TranslationSetupDialog.module.scss';
import { StyledBox } from './TranslationSetupDialog.styled';
import { getFormFieldKey } from './TranslationSetupDialog.helpers';

interface Props {
  index: number;
  item: TranslationContentList['content'][number];
  fromLanguage: string;
  toLanguage: string;
  isChanged: boolean;
  onSave: (textKey: string) => void;
  activeTab: number;
  getChangedValues: () => Record<
    string,
    {
      oldValue: string;
      newValue: string;
    }
  >;
}

const TranslationItem: FC<Props> = ({ index, item, fromLanguage, toLanguage, onSave, activeTab, getChangedValues }) => {
  const { getTranslation } = useCustomTranslation();
  const dispatch = useAppDispatch();

  const { control, setValue } = useFormContext<TranslationSetupForm>();
  const { selectedProject } = useAppSelector((state) => state.projects);

  const language = useWatch({ control, name: 'language' });
  const updatedTranslation = useWatch({ control, name: getFormFieldKey(item.textKey, activeTab) });
  const projectAnalysisActiveTab = useWatch({ control, name: 'projectAnalysisActiveTab' });
  const projectTranslationActiveTab = useWatch({ control, name: 'projectTranslationActiveTab' });

  const [translateGeneral, { isLoading: loadingTranslateGeneral }] = useTranslateGeneralContentTranslationMutation();
  const [translateProject, { isLoading: loadingTranslateProject }] = useTranslateProjectContentDataMutation();

  const onFieldBlur = () => {
    if (updatedTranslation !== item.translatedText) {
      onSave(getFormFieldKey(item.textKey, activeTab));
    }
  };

  const translateText = useCallback(
    async (textKey: string, sourceText: string) => {
      try {
        const method =
          projectTranslationActiveTab === +SETUP_LANGUAGE_TABS.GENERAL ? translateGeneral : translateProject;

        const args =
          projectTranslationActiveTab === +SETUP_LANGUAGE_TABS.GENERAL
            ? {
                languageId: language,
                languagePool: PROJECT_SETUP_LANGUAGE_OPTIONS[projectAnalysisActiveTab],
                body: { textKey, sourceText },
              }
            : {
                languageId: language,
                languagePool: PROJECT_SETUP_LANGUAGE_OPTIONS[projectAnalysisActiveTab],
                businessId: selectedProject?.selectedBusinessId,
                body: { textKey, sourceText },
              };

        const response = await method(args).unwrap();

        if (response.payload && response.payload?.translatedText !== item.translatedText) {
          setValue(getFormFieldKey(textKey, activeTab), response.payload?.translatedText);
          dispatch(setSetupLanguageChanges(getChangedValues()));
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      language,
      projectTranslationActiveTab,
      projectAnalysisActiveTab,
      selectedProject,
      translateGeneral,
      translateProject,
      setValue,
      activeTab,
    ],
  );

  return (
    <StyledBox className={styles.setupItem} index={index}>
      <Box className={styles.setupRow}>
        <Typography variant="subtitle1">{`${fromLanguage}: `}</Typography>
        <Tooltip arrow placement="top" title={item.sourceText.length > TRANSLATION_MAX_LENGTH ? item.sourceText : null}>
          <Typography variant="body1">
            <span dangerouslySetInnerHTML={{ __html: truncateString(item.sourceText, TRANSLATION_MAX_LENGTH) }} />
          </Typography>
        </Tooltip>
      </Box>
      <Box className={styles.setupRow}>
        <Typography variant="subtitle1">{`${toLanguage}: `}</Typography>
        <ControlledTextField
          name={getFormFieldKey(item.textKey, activeTab)}
          control={control}
          rulesRequired={false}
          onBlur={onFieldBlur}
        />
        <Button
          variant="outlined"
          size="medium"
          onClick={() => translateText(item.textKey, item.sourceText || String(updatedTranslation))}
          endIcon={
            loadingTranslateGeneral || loadingTranslateProject ? <CircularProgress size={DEFAULT_ICON_WIDTH} /> : null
          }
          disabled={loadingTranslateGeneral || loadingTranslateProject}
        >
          {getTranslation('CvaInput.Home103', 'buttons.translate')}
        </Button>
      </Box>
    </StyledBox>
  );
};

export default memo(TranslationItem);
